import createDOMPurify, { type Config as DOMPurifyConfig } from 'dompurify'
import { Marked, type Tokens } from 'marked'
import { logger } from '../../lib/logger'
import { resolveLegacyHelpLink } from './helpTree'
import type { HelpIndex } from './types'

export interface HelpTocEntry {
  id: string
  text: string
}

export interface RenderedHelpArticle {
  html: string
  toc: HelpTocEntry[]
}

// Video hosts an article may embed. Anything else stays a plain link.
const VIDEO_EMBEDS: Array<{ match: RegExp; embed: (id: string) => string }> = [
  {
    match: /^https:\/\/www\.loom\.com\/(?:share|embed)\/([a-z0-9]+)/i,
    embed: (id) => `https://www.loom.com/embed/${id}`,
  },
  {
    match:
      /^https:\/\/(?:www\.)?youtube\.com\/watch\?v=([A-Za-z0-9_-]+)|^https:\/\/youtu\.be\/([A-Za-z0-9_-]+)/i,
    embed: (id) => `https://www.youtube-nocookie.com/embed/${id}`,
  },
  {
    match: /^https:\/\/vimeo\.com\/(\d+)/i,
    embed: (id) => `https://player.vimeo.com/video/${id}`,
  },
]

const EMBED_SRC_RE =
  /^https:\/\/(?:www\.loom\.com\/embed\/[a-z0-9]+|www\.youtube-nocookie\.com\/embed\/[A-Za-z0-9_-]+|player\.vimeo\.com\/video\/\d+)$/i

const SANITIZE_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'em',
    'code',
    'pre',
    'ul',
    'ol',
    'li',
    'blockquote',
    'h2',
    'h3',
    'h4',
    'hr',
    'a',
    'img',
    'iframe',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'div',
  ],
  ALLOWED_ATTR: [
    'href',
    'target',
    'rel',
    'src',
    'alt',
    'loading',
    'class',
    'id',
    'data-in-app',
    'allowfullscreen',
    'allow',
    'referrerpolicy',
    'title',
  ],
  // In-app article links are relative; everything else must be absolute.
  ALLOWED_URI_REGEXP:
    /^(?:https?:|mailto:|tel:|\/help\/|\/onboarding\/[A-Za-z0-9_-]+\/help\/)/i,
  // DOMPurify holds every attribute value to the URI pattern above unless the
  // attribute is declared URI-safe, which would strip `target="_blank"`.
  ADD_URI_SAFE_ATTR: [
    'target',
    'rel',
    'loading',
    'data-in-app',
    'id',
    'allow',
    'allowfullscreen',
    'referrerpolicy',
    'title',
  ],
}

// Own instance: the hook below must not leak into other sanitising in the app.
let purifier: ReturnType<typeof createDOMPurify> | null = null
function getPurifier() {
  if (!purifier) {
    purifier = createDOMPurify(window)
    purifier.addHook('uponSanitizeElement', (node, data) => {
      if (data.tagName !== 'iframe') return
      const element = node as Element
      const src = element.getAttribute?.('src') ?? ''
      if (!EMBED_SRC_RE.test(src)) element.parentNode?.removeChild(element)
    })
  }
  return purifier
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function plainText(html: string): string {
  return html
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim()
}

function headingId(text: string, taken: Set<string>): string {
  const base =
    text
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'abschnitt'
  let id = `h-${base}`
  for (let n = 2; taken.has(id); n++) id = `h-${base}-${n}`
  taken.add(id)
  return id
}

function videoEmbed(href: string): string | null {
  for (const { match, embed } of VIDEO_EMBEDS) {
    const found = href.match(match)
    const id = found?.[1] ?? found?.[2]
    if (id) return embed(id)
  }
  return null
}

// Articles are written by our own team, but the body is still rendered as
// untrusted input: Markdown may carry raw HTML, so it is sanitised after parsing.
//
// The table of contents lists the two top heading levels, like the old help
// center did.
//
// Links to other help articles become in-app links: through `index` inside the
// help center, or through `legacyLink` where the reader has its own addresses.
export function renderHelpArticle(
  markdown: string,
  index: HelpIndex | null,
  legacyLink?: (url: string) => string | null
): RenderedHelpArticle {
  const toc: HelpTocEntry[] = []
  const takenIds = new Set<string>()
  const marked = new Marked({ gfm: true, breaks: false })
  marked.use({
    renderer: {
      heading({ tokens, depth }: Tokens.Heading) {
        const inner = this.parser.parseInline(tokens)
        // Article titles are the page's h1, so body headings start at h2.
        const level = Math.min(Math.max(depth, 2), 4)
        const text = plainText(inner)
        if (!text) return ''
        const id = headingId(text, takenIds)
        if (level <= 3) toc.push({ id, text })
        return `<h${level} id="${id}">${inner}</h${level}>`
      },
      link({ href, tokens }: Tokens.Link) {
        const text = this.parser.parseInline(tokens)
        const target = href ?? ''
        const embed = videoEmbed(target)
        if (embed) {
          return `<div class="help-video"><iframe src="${escapeAttribute(
            embed
          )}" loading="lazy" allowfullscreen="allowfullscreen" referrerpolicy="strict-origin-when-cross-origin" allow="fullscreen; picture-in-picture; encrypted-media" title="Video"></iframe></div>`
        }
        const inAppPath = legacyLink
          ? legacyLink(target)
          : resolveLegacyHelpLink(target, index)?.path
        if (inAppPath) {
          return `<a href="${escapeAttribute(
            inAppPath
          )}" data-in-app="true">${text}</a>`
        }
        return `<a href="${escapeAttribute(
          target
        )}" target="_blank" rel="noopener noreferrer">${text}</a>`
      },
      image({ href, text }: Tokens.Image) {
        if (!href || !/^https:\/\//i.test(href)) return ''
        const src = escapeAttribute(href)
        // Like the old help center: the image opens full size in a new tab.
        return `<a href="${src}" target="_blank" rel="noopener noreferrer" class="help-image"><img src="${src}" alt="${escapeAttribute(
          text ?? ''
        )}" loading="lazy"></a>`
      },
      table(token: Tokens.Table) {
        const header = token.header
          .map((cell) => `<th>${this.parser.parseInline(cell.tokens)}</th>`)
          .join('')
        const rows = token.rows
          .map(
            (row) =>
              `<tr>${row
                .map(
                  (cell) => `<td>${this.parser.parseInline(cell.tokens)}</td>`
                )
                .join('')}</tr>`
          )
          .join('')
        return `<div class="help-table"><table><thead><tr>${header}</tr></thead><tbody>${rows}</tbody></table></div>`
      },
    },
  })
  try {
    const parsed = marked.parse(markdown, { async: false })
    return {
      html: getPurifier().sanitize(
        typeof parsed === 'string' ? parsed : '',
        SANITIZE_CONFIG
      ),
      toc,
    }
  } catch (error) {
    logger.warn('Failed to render help article', { error: String(error) })
    return {
      html: getPurifier().sanitize(markdown, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      }),
      toc: [],
    }
  }
}

export function renderHelpMarkdown(
  markdown: string,
  index: HelpIndex | null
): string {
  return renderHelpArticle(markdown, index).html
}
